import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Tooltip,
  TextField,
  InputAdornment,
  Autocomplete
} from '@mui/material';
import { Add, MoreVert, /* Delete, */ Search, Refresh, Info, Link as LinkIcon } from '@mui/icons-material';
import EditIcon from '../icons/EditIcon';
import { useNotification } from '../layouts/Layout';
import subcontractorService, { Subcontractor } from '../../services/subcontractorService';
import AddSubcontractorForm from './AddSubcontractorForm';
import EditSubcontractorForm from './EditSubcontractorForm';
import SubcontractorDetailsDialog from './SubcontractorDetailsDialog';
import protocolService from '../../services/protocolService';

interface SubcontractorsTabProps {
  projectName: string;
  projectId?: string;
  openDetailsForSubId?: string;
}

const SubcontractorsTab: React.FC<SubcontractorsTabProps> = ({ projectName, projectId, openDetailsForSubId }) => {
  const [subcontractors, setSubcontractors] = useState<Subcontractor[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addFormOpen, setAddFormOpen] = useState(false);
  const [editFormOpen, setEditFormOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedSubcontractor, setSelectedSubcontractor] = useState<Subcontractor | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSubcontractors, setFilteredSubcontractors] = useState<Subcontractor[]>([]);
  const { showNotification } = useNotification();
  const [latestSinceStartBySub, setLatestSinceStartBySub] = useState<Record<string, number>>({});
  const openedRef = useRef(false);

  // Pobieranie podwykonawców
  const fetchSubcontractors = async () => {
    if (!projectId) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await subcontractorService.getSubcontractorsByProjectId(projectId);
      setSubcontractors(data);
      setFilteredSubcontractors(data);
      // pobierz protokoły i zbuduj mapę ostatniego value_since_start per podwykonawca
      try {
        const list: any = await protocolService.getByProject(projectId);
        const items: any[] = Array.isArray(list) ? list : (list?.data || []);
        const map: Record<string, { seq: number; date: string; val: number; latestProgress?: number }> = {};
        for (const it of items) {
          // Dla wartości zafakturowano i zaawansowania preferuj POKR (jeśli istnieje), w przeciwnym razie najnowszy POCR
          const sid = String(it.subcontractor_id);
          const date = it.created_at || it.protocol_date || '';
          const val = Number(it.value_since_start || 0);
          const prev = map[sid];
          if (it.type === 'POKR') {
            // POKR ma pierwszeństwo – zawsze nadpisuje
            map[sid] = { seq: Number.MAX_SAFE_INTEGER, date, val, latestProgress: Number(it.progress_percent || 0) };
            continue;
          }
          if (it.type !== 'POCR') continue;
          const seq = Number(it.sequence_number || 0);
          // Najnowszy POCR – zarówno do wartości zafakturowanej, jak i do zaawansowania
          if (!prev || seq > prev.seq || (seq === prev.seq && date > prev.date)) {
            map[sid] = { ...(prev || {}), seq, date, val, latestProgress: Number(it.progress_percent || 0) };
          }
        }
        const flat: Record<string, number> = {};
        Object.keys(map).forEach(k => flat[k] = map[k].val);
        setLatestSinceStartBySub(flat);

        // zaktualizuj listy z wstrzykniętym zaawansowaniem (najnowszy POCR)
        const withProgress = data.map(sc => {
          const m = map[String(sc.id)];
          if (!m || m.latestProgress == null) return sc;
          return { ...sc, progress: m.latestProgress } as any;
        });
        setSubcontractors(withProgress);
        // jeżeli nie ma aktywnego filtra, nadpisz również widok
        setFilteredSubcontractors(prev => (
          (searchTerm.trim() === '') ? withProgress : prev.map(sc => {
            const m = map[String(sc.id)];
            if (!m || m.latestProgress == null) return sc;
            return { ...sc, progress: m.latestProgress } as any;
          })
        ));
      } catch {}
    } catch (error) {
      console.error('Błąd podczas pobierania podwykonawców:', error);
      setError('Nie udało się pobrać podwykonawców. Spróbuj ponownie później.');
      showNotification('Nie udało się pobrać podwykonawców. Spróbuj ponownie później.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Pobieranie podwykonawców przy pierwszym renderowaniu
  useEffect(() => {
    if (projectId) {
      fetchSubcontractors();
    }
  }, [projectId]);

  // Otwieranie szczegółów z deeplinka, gdy lista się załaduje – tylko raz
  useEffect(() => {
    if (!openDetailsForSubId || openedRef.current) return;
    const found = filteredSubcontractors.find(s => String(s.id) === String(openDetailsForSubId));
    if (found) {
      setSelectedSubcontractor(found);
      setDetailsOpen(true);
      openedRef.current = true;
      // Usuń parametry z adresu, żeby nie otwierać ponownie przy każdej wizycie
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('open');
        url.searchParams.delete('projectId');
        url.searchParams.delete('subId');
        const qs = url.searchParams.toString();
        window.history.replaceState({}, '', url.pathname + (qs ? `?${qs}` : ''));
      } catch {}
    }
  }, [openDetailsForSubId, filteredSubcontractors]);

  // Obsługa menu kontekstowego
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, subcontractor: Subcontractor) => {
    setAnchorEl(event.currentTarget);
    setSelectedSubcontractor(subcontractor);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // Obsługa usuwania podwykonawcy
  const handleDeleteSubcontractor = async () => {
    if (selectedSubcontractor?.id) {
      try {
        setLoading(true);
        await subcontractorService.deleteSubcontractor(selectedSubcontractor.id);
        showNotification('Podwykonawca został usunięty', 'success');
        fetchSubcontractors(); // Odświeżenie listy podwykonawców
        handleCloseMenu();
      } catch (error) {
        console.error('Błąd podczas usuwania podwykonawcy:', error);
        showNotification('Nie udało się usunąć podwykonawcy. Spróbuj ponownie później.', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  // Obsługa dodawania podwykonawcy
  const handleAddSubcontractor = (subcontractor: Subcontractor) => {
    setSubcontractors(prev => [...prev, subcontractor]);
    setFilteredSubcontractors(prev => [...prev, subcontractor]);
  };

  // Obsługa aktualizacji podwykonawcy
  const handleUpdateSubcontractor = (updatedSubcontractor: Subcontractor) => {
    setSubcontractors(prev => 
      prev.map(item => item.id === updatedSubcontractor.id ? updatedSubcontractor : item)
    );
    setFilteredSubcontractors(prev => 
      prev.map(item => item.id === updatedSubcontractor.id ? updatedSubcontractor : item)
    );
  };

  // Obsługa wyszukiwania
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value;
    setSearchTerm(searchValue);
    
    if (searchValue.trim() === '') {
      setFilteredSubcontractors(subcontractors);
    } else {
      const filtered = subcontractors.filter(
        subcontractor => 
          subcontractor.name.toLowerCase().includes(searchValue.toLowerCase()) ||
          subcontractor.contract_number.toLowerCase().includes(searchValue.toLowerCase()) ||
          subcontractor.contract_subject.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredSubcontractors(filtered);
    }
  };

  // Formatowanie wartości pieniężnych
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(value);
  };

  // Formatowanie procentów
  const formatPercent = (value: number) => {
    return `${value}%`;
  };

  // Obliczanie sumy wartości umów
  const totalContractValue = filteredSubcontractors.reduce((sum, item) => sum + (Number(item.contract_value) || 0), 0);
  
  // Obliczanie sumy zafakturowanych kwot
  const totalInvoicedAmount = filteredSubcontractors.reduce((sum, item) => sum + (latestSinceStartBySub[String(item.id)] ?? 0), 0);
  // Globalne zaawansowanie = suma zafakturowano / suma wartość * 100
  const globalProgress = totalContractValue > 0 ? (totalInvoicedAmount / totalContractValue) * 100 : 0;
  // PDZ liczony z POKR (jeśli jest) albo z najnowszego POCR: contract_value - value_since_start
  const totalPdz = filteredSubcontractors.reduce((sum, item) => {
    const sinceStart = latestSinceStartBySub[String(item.id)];
    const pdz = typeof sinceStart === 'number' ? Math.max(0, Number(item.contract_value || 0) - sinceStart) : (item.pdz || 0);
    return sum + pdz;
  }, 0);

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Podwykonawcy
      </Typography>
      
      {/* Pasek filtrowania i akcji */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          {/* Pole wyszukiwania */}
          <TextField
            placeholder="Szukaj po nazwie..."
            value={searchTerm}
            onChange={handleSearchChange}
            variant="outlined"
            size="small"
            sx={{ width: '350px' }}
            InputProps={{
              style: { fontSize: '0.75rem' },
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#999', fontSize: '16px' }} />
                </InputAdornment>
              )
            }}
            InputLabelProps={{
              style: { fontSize: '0.75rem' }
            }}
          />
        </Box>
        
        {/* Przyciski akcji */}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setAddFormOpen(true)}
            size="small"
            sx={{ 
              backgroundColor: '#004273',
              '&:hover': { backgroundColor: '#003660' }
            }}
          >
            Dodaj
          </Button>
        </Box>
      </Box>
      
      <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #eee', maxHeight: '450px', overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
              <TableCell width="120px" sx={{ whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f5f5f5' }}>Nr umowy</TableCell>
              <TableCell width="180px" sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f5f5f5' }}>Nazwa</TableCell>
              <TableCell width="250px" sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f5f5f5' }}>Przedmiot umowy</TableCell>
              <TableCell align="center" width="120px" sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f5f5f5' }}>TWU</TableCell>
              <TableCell align="center" width="80px" sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f5f5f5' }}>PD</TableCell>
              <TableCell align="center" width="120px" sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f5f5f5' }}>Zaawansowanie</TableCell>
              <TableCell align="right" width="120px" sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f5f5f5' }}>Wartość</TableCell>
              <TableCell align="right" width="120px" sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f5f5f5' }}>Zafakturowano</TableCell>
              <TableCell align="right" width="120px" sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f5f5f5' }}>PDZ</TableCell>
              <TableCell width="36px" align="center" sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f5f5f5' }} />
              <TableCell width="36px" align="center" sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f5f5f5' }} />
              <TableCell width="50px" align="center" sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f5f5f5' }}>Akcja</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress size={40} sx={{ my: 2 }} />
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="error">{error}</Typography>
                </TableCell>
              </TableRow>
            ) : filteredSubcontractors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" sx={{ py: 2 }}>
                    Brak podwykonawców do wyświetlenia.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredSubcontractors.map((subcontractor) => (
                <TableRow key={subcontractor.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>{subcontractor.contract_number}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{subcontractor.name}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>
                    {subcontractor.contract_subject && subcontractor.contract_subject.length > 50
                      ? `${subcontractor.contract_subject.slice(0, 50)}…`
                      : subcontractor.contract_subject}
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: '0.75rem' }}>{subcontractor.completion_date}</TableCell>
                  <TableCell align="center" sx={{ fontSize: '0.75rem' }}>{subcontractor.days_remaining}</TableCell>
                  <TableCell align="center" sx={{ fontSize: '0.75rem' }}>{formatPercent(subcontractor.progress)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{formatCurrency(subcontractor.contract_value)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{formatCurrency(latestSinceStartBySub[String(subcontractor.id)] ?? 0)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                    {formatCurrency(typeof latestSinceStartBySub[String(subcontractor.id)] === 'number'
                      ? Math.max(0, Number(subcontractor.contract_value || 0) - (latestSinceStartBySub[String(subcontractor.id)] || 0))
                      : subcontractor.pdz)}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => { setSelectedSubcontractor(subcontractor); setDetailsOpen(true); }}>
                      <Info fontSize="small" />
                    </IconButton>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" component="a" href={subcontractor.drive_folder_id ? `https://drive.google.com/drive/folders/${subcontractor.drive_folder_id}` : undefined} target="_blank" rel="noreferrer">
                      <LinkIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={(e) => handleOpenMenu(e, subcontractor)}>
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
            <TableRow sx={{ position: 'sticky', bottom: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}>
              <TableCell colSpan={5} sx={{ fontWeight: 'bold', borderTop: '1px solid #eee', fontSize: '0.75rem' }} />
              <TableCell align="center" sx={{ fontWeight: 'bold', borderTop: '1px solid #eee', fontSize: '0.75rem' }}>
                {`${globalProgress.toFixed(2)}%`}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', borderTop: '1px solid #eee', fontSize: '0.75rem' }}>
                {formatCurrency(totalContractValue)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', borderTop: '1px solid #eee', fontSize: '0.75rem' }}>
                {formatCurrency(totalInvoicedAmount)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', borderTop: '1px solid #eee', fontSize: '0.75rem' }}>
                {formatCurrency(totalPdz)}
              </TableCell>
              <TableCell colSpan={3} sx={{ borderTop: '1px solid #eee', fontSize: '0.75rem' }}></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Menu kontekstowe */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => {
          if (selectedSubcontractor) {
            // Otwórz formularz edycji
            setEditFormOpen(true);
            handleCloseMenu();
          }
        }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edytuj
        </MenuItem>
        <MenuItem onClick={handleCloseMenu}>
          <Add fontSize="small" sx={{ mr: 1 }} />
          Dodaj POCR
        </MenuItem>
        <MenuItem onClick={handleCloseMenu}>
          <Add fontSize="small" sx={{ mr: 1 }} />
          Dodaj POKR
        </MenuItem>
        <MenuItem onClick={handleCloseMenu}>
          <Add fontSize="small" sx={{ mr: 1 }} />
          Dodaj Wezwanie
        </MenuItem>
      </Menu>

      {/* Formularz dodawania podwykonawcy */}
      <AddSubcontractorForm
        open={addFormOpen}
        onClose={() => setAddFormOpen(false)}
        onAdd={handleAddSubcontractor}
        projectId={projectId || ''}
      />

      {/* Formularz edycji podwykonawcy */}
      <EditSubcontractorForm
        open={editFormOpen}
        onClose={() => {
          setEditFormOpen(false);
          setSelectedSubcontractor(null);
        }}
        onUpdate={(updatedSubcontractor) => {
          handleUpdateSubcontractor(updatedSubcontractor);
          setSelectedSubcontractor(null);
        }}
        subcontractor={selectedSubcontractor}
        onDeleted={(id) => {
          // usuń lokalnie, aby rekord zniknął natychmiast, a potem dociągnij dane
          setSubcontractors(prev => prev.filter(s => String(s.id) !== String(id)));
          setFilteredSubcontractors(prev => prev.filter(s => String(s.id) !== String(id)));
          setEditFormOpen(false);
          setSelectedSubcontractor(null);
          // dociągnij świeżą listę z backendu (dla pewności)
          fetchSubcontractors();
        }}
      />

      {/* Karta szczegółów podwykonawcy */}
      <SubcontractorDetailsDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        subcontractor={selectedSubcontractor}
        onProtocolsUpdated={() => fetchSubcontractors()}
      />
    </Box>
  );
};

export default SubcontractorsTab;
